import { useState } from "react";
import parse from "html-react-parser";

import {
  Box,
  Grid,
  Paper,
  Typography,
  Tabs,
  Tab,
  Divider,
  Button,
  IconButton,
  Chip,
  useTheme,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonIcon from "@mui/icons-material/Person";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faCertificate,
  faRecycle,
  faHelmetSafety,
  faFileCertificate,
  faPersonDigging,
  faCalendar,
} from "@fortawesome/pro-solid-svg-icons";

import { formatDate } from "@aclymatepackages/formatters";
import { hexToRgba } from "@aclymatepackages/converters";
import { AnimatedLogo } from "@aclymatepackages/atoms";
import GoogleMap from "@aclymatepackages/google-maps";

import Link from "../../atoms/mui/Link";
import GrayPaper from "../../atoms/GrayPaper";
import SlideLayout from "../../layouts/SlideLayout";

const ProjectOverviewTab = ({ project }) => {
  const {
    primary_image_url: image,
    description,
    registry_id,
    registry_url,
  } = project;

  const chipsArray = [
    {
      field: "registry_name",
      label: "Registry",
      icon: faCertificate,
      formatter: (val) => (
        <>
          {val} {registry_id && `(${registry_id})`}{" "}
          {registry_url && (
            <IconButton size="small">
              <Link href={registry_url}>
                <OpenInNewIcon />
              </Link>
            </IconButton>
          )}
        </>
      ),
    },
    { field: "impact_type", label: "Impact Type", icon: faRecycle },
    { field: "developer", label: "Developer", icon: faHelmetSafety },
    { field: "verifier", label: "Verifier", icon: faFileCertificate },
    {
      field: "activity_types",
      label: "Activity Types",
      formatter: (vals) => vals.join(", "),
      icon: faPersonDigging,
    },
    {
      field: "lifetime",
      label: "Project Lifetime",
      formatter: ({ start_year, end_year }) =>
        `${start_year} - ${end_year} (${end_year - start_year} years)`,
      icon: faCalendar,
    },
  ];

  const displayChips = chipsArray
    .filter(({ field }) => project[field])
    .map(({ field, formatter, ...otherProps }) => {
      const value = project[field];

      if (formatter) {
        return { ...otherProps, value: formatter(value) };
      }

      return { ...otherProps, value };
    });

  return (
    <Grid container direction="column" spacing={4}>
      <Grid item>
        <Grid container direction="column" spacing={1}>
          {displayChips.map(({ label, value, icon }) => (
            <Grid
              item
              container
              spacing={1}
              alignItems="center"
              wrap="nowrap"
              key={label}
            >
              <Grid item xs={3}>
                <Box
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Chip label={label} icon={<FontAwesomeIcon icon={icon} />} />
                </Box>
              </Grid>
              <Grid item xs={9}>
                <Typography variant="h6">{value}</Typography>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid item>
        <Paper style={{ overflow: "hidden" }}>
          <Box
            style={{
              height: 250,
              backgroundImage: `url('${image}')`,
              backgroundPosition: "center center",
              backgroundSize: "cover",
            }}
          />
        </Paper>
      </Grid>
      <Grid item>
        <Typography variant="body2">{description}</Typography>
      </Grid>
    </Grid>
  );
};

const ParagraphTab = ({ text, image }) => {
  const { description, image_url } = image || {};

  const dueDiligenceParagraphs = parse(text)
    .split("\n")
    .filter((p) => p && p.trim());

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        {dueDiligenceParagraphs.map((paragraph, index) => (
          <Typography
            variant={paragraph.length < 30 ? "h5" : "body2"}
            paragraph={paragraph.length >= 30}
            key={`due-diligence-par-${index}`}
          >
            {paragraph.trim()}
          </Typography>
        ))}
      </Grid>
      {image && (
        <Grid item>
          <img
            alt="Due Diligence Figure"
            src={image_url}
            style={{ maxWidth: "100%" }}
          />
          <Typography
            style={{ lineHeight: 0.6 }}
            variant="caption"
            color="textSecondary"
          >
            {description}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

const UnSdgsTab = ({ unSdgs = [] }) => {
  if (unSdgs.length) {
    return (
      <Grid container spacing={1} justifyContent="space-around">
        {unSdgs.map((number) => (
          <Grid item key={`sdg-${number}`}>
            <Link
              href={`https://sdgs.un.org/goals/goal${number}`}
              target="_blank"
              rel="noopener"
            >
              <img
                src={`https://aclymate.app/images/un-sdgs/${number}.png`}
                alt={`UN SDG ${number}`}
                width={150}
              />
            </Link>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container justifyContent="center">
      <Grid item>
        <Typography variant="h4" align="center">
          This project has not registered any UN SDG goals with Aclymate.
        </Typography>
      </Grid>
    </Grid>
  );
};

const NewArticlesTab = ({ news_articles }) => (
  <Grid container direction="column" spacing={2}>
    {news_articles.map(
      ({ author, cnaught_take, published_on, title, url }, idx) => (
        <Grid item key={`news-article-${idx}`}>
          <GrayPaper>
            <Box p={2}>
              <Grid container spacing={2} direction="column">
                <Grid
                  item
                  container
                  justifyContent="space-between"
                  spacing={8}
                  wrap="nowrap"
                >
                  <Grid item>
                    <Typography variant="h5">{title}</Typography>
                  </Grid>
                  <Grid item>
                    <Link href={url} target="_blank" rel="noopener">
                      <IconButton>
                        <OpenInNewIcon />
                      </IconButton>
                    </Link>
                  </Grid>
                </Grid>
                <Grid item>
                  <Typography variant="body2">{cnaught_take}</Typography>
                </Grid>
              </Grid>
            </Box>
            <Divider />
            <Box p={2}>
              <Grid container justifyContent="space-around" alignItems="center">
                <Grid item>
                  <Box display="flex" alignItems="center" columnGap={1}>
                    <PersonIcon fontSize="small" />
                    <Typography variant="caption" color="textSecondary">
                      By {author}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item>
                  <Box display="flex" alignItems="center" columnGap={1}>
                    <FontAwesomeIcon icon={faCalendar} />
                    <Typography variant="caption" color="textSecondary">
                      Published on {formatDate(published_on)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </GrayPaper>
        </Grid>
      ),
    )}
  </Grid>
);

const NotableBuyersTab = ({ notable_buyers }) => (
  <Grid
    container
    spacing={4}
    justifyContent="space-around"
    alignItems="flex-end"
  >
    {notable_buyers.map(({ logo_url, name }) => (
      <Grid item xs={4}>
        <Box
          display="flex"
          flexDirection="column"
          rowGap={1}
          alignItems="center"
        >
          <img src={logo_url} alt="Notable Buyer Logo" width={150} />
          <Typography variant="subtitle1" color="textSecondary" align="center">
            {name}
          </Typography>
        </Box>
      </Grid>
    ))}
  </Grid>
);

const ProjectDetailsSlider = ({
  project = {},
  projectLoading,
  onSlideClose,
}) => {
  const theme = useTheme();

  const {
    name,
    due_diligence,
    due_diligence_figure,
    beyond_carbon,
    location_latitude: latitude,
    location_longitude: longitude,
    un_sdg_goals,
    news_articles,
    notable_buyers,
  } = project;

  const [selectedViewIdx, setSelectedViewIdx] = useState(0);

  const dueDiligenceTabArray = due_diligence
    ? [
        {
          label: "Due Diligence",
          value: "due_diligence",
          content: (
            <ParagraphTab text={due_diligence} image={due_diligence_figure} />
          ),
        },
      ]
    : [];

  const beyondCardonTabArray = beyond_carbon
    ? [
        {
          label: "Beyond Carbon",
          value: "beyond_carbon",
          content: <ParagraphTab text={beyond_carbon} />,
        },
      ]
    : [];

  const unSdgsTabArray =
    un_sdg_goals && un_sdg_goals.length
      ? [
          {
            label: "UN SDGs",
            value: "sdgs",
            content: <UnSdgsTab unSdgs={un_sdg_goals} />,
          },
        ]
      : [];

  const newsArticlesTabArray =
    news_articles && news_articles.length
      ? [
          {
            label: "News Articles",
            value: "news_articles",
            content: <NewArticlesTab news_articles={news_articles} />,
          },
        ]
      : [];

  const notableBuyersTabArray =
    notable_buyers && notable_buyers.length
      ? [
          {
            label: "Notable Buyers",
            value: "notable_buyers",
            content: <NotableBuyersTab notable_buyers={notable_buyers} />,
          },
        ]
      : [];

  const views = [
    {
      label: "Overview",
      value: "overview",
      content: <ProjectOverviewTab project={project} />,
    },
    ...dueDiligenceTabArray,
    ...beyondCardonTabArray,
    ...unSdgsTabArray,
    ...newsArticlesTabArray,
    ...notableBuyersTabArray,
  ];

  const onTabClick = (value) => {
    const tabValueIndex = views.findIndex((tab) => tab.value === value);
    return setSelectedViewIdx(tabValueIndex);
  };

  const { value, content } = views[selectedViewIdx] || {};

  return (
    <>
      {!projectLoading && (
        <SlideLayout
          isSlideOpen
          setIsSlideOpen={onSlideClose}
          header={<Typography variant="h3">{name}</Typography>}
          tagInputs={
            <>
              <Tabs
                value={value}
                onChange={(_, value) => onTabClick(value)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {views.map(({ label, value }, idx) => (
                  <Tab
                    label={label}
                    value={value}
                    key={`object-details-view-tab-${idx}`}
                  />
                ))}
              </Tabs>
              <Divider />
            </>
          }
          content={
            projectLoading ? (
              <Box display="flex" alignItems="center" justifyContent="center">
                <AnimatedLogo />
              </Box>
            ) : (
              content
            )
          }
          footer={
            value === "sdgs" && (
              <Grid
                container
                justifyContent="center"
                spacing={2}
                direction="row"
              >
                <Grid item>
                  <img
                    alt="UN SDGs Banner"
                    src="https://aclymate.app/images/un-sdgs/un_sdgs_banner.png"
                    style={{ width: "350px" }}
                  />
                </Grid>
                <Grid item>
                  <Link
                    href="https://sdgs.un.org/goals"
                    target="_blank"
                    rel="noopener"
                    underline="none"
                  >
                    <Button variant="contained" color="secondary">
                      Learn More
                    </Button>
                  </Link>
                </Grid>
              </Grid>
            )
          }
          footerColor={
            value === "sdgs" && hexToRgba(theme.palette.secondary.main, 0.1)
          }
          leftContent={
            <Paper
              style={{ height: "40vh", width: "40vw", overflow: "hidden" }}
              elevation={3}
            >
              <GoogleMap
                height="100%"
                width="100%"
                markers={[
                  {
                    latLng: { x: latitude, y: longitude },
                    marker: {
                      url: "/images/platform/icons/location-dot-solid.svg",
                      scaledSize: new window.google.maps.Size(40, 40),
                    },
                  },
                ]}
              />
            </Paper>
          }
        />
      )}
    </>
  );
};
export default ProjectDetailsSlider;
